import type { CustomCell, CustomRenderer } from '@glideapps/glide-data-grid'
import { getMiddleCenterBias, GridCellKind } from '@glideapps/glide-data-grid'

export const stringToHue = (str: string): number => {
  let hash = 0

  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)

    // eslint-disable-next-line no-bitwise
    hash = (hash << 5) - hash + char
    // eslint-disable-next-line no-bitwise
    hash &= hash // Convert to 32bit integer
  }

  return Math.abs(hash % 360)
}

// Simple HSL-based pill colors without chroma.js dependency
export const hueToPillColorLight = (hue: number): PillColor => {
  const base = `hsl(${hue}, 70%, 94%)`
  const border = `hsl(${hue}, 70%, 86%)`
  const text = `hsl(${hue}, 70%, 44%)`

  return { base, border, text }
}

export const hueToPillColorDark = (hue: number): PillColor => {
  const base = `hsl(${hue}, 40%, 40%)`
  const border = `hsl(${hue}, 40%, 40%)`
  const text = `hsl(${hue}, 40%, 95%)`

  return { base, border, text }
}

export interface PillColor {
  readonly base: string
  readonly border: string
  readonly text: string
}

/** Truncate `label` with an ellipsis so it fits `maxWidth` in the current ctx font. */
export const truncateToWidth = (
  ctx: CanvasRenderingContext2D,
  label: string,
  maxWidth: number,
): string => {
  if (ctx.measureText(label).width <= maxWidth) {
    return label
  }

  const ellipsis = '…'
  let low = 0
  let high = label.length
  while (low < high) {
    const mid = Math.ceil((low + high) / 2)
    if (ctx.measureText(label.slice(0, mid) + ellipsis).width <= maxWidth) {
      low = mid
    } else {
      high = mid - 1
    }
  }

  return label.slice(0, low) + ellipsis
}

interface PillCellProps {
  readonly kind: 'auto-tags-cell'
  readonly value: string
  readonly color: {
    readonly text: string
    readonly border: string
    readonly base: string
  }
}

export type PillCell = CustomCell<PillCellProps>

export const pillCellRenderer = (): CustomRenderer<PillCell> => ({
  draw: (args, cell) => {
    const { rect, ctx, theme } = args
    const { x, y, height } = rect
    const { base, border, text } = cell.data.color
    // Keep the pill inside the cell: truncate the label instead of letting the
    // canvas text spill into the neighbouring column and get hard-clipped.
    const maxTextWidth = rect.width - 30
    const label = truncateToWidth(ctx, cell.data.value, maxTextWidth)
    const textMetrics = ctx.measureText(label)
    const textWidth = textMetrics.width
    const textHeight = textMetrics.fontBoundingBoxAscent + textMetrics.fontBoundingBoxDescent
    const tileWidth = textWidth + 10
    const tileHeight = textHeight * 1.2

    // Draw the tile
    if ('roundRect' in ctx) {
      ctx.fillStyle = base
      ctx.strokeStyle = border
      ctx.beginPath()
      ctx.roundRect(x + 10, y + height / 2 - tileHeight / 2, tileWidth, tileHeight, 5)
      ctx.stroke()
      ctx.fill()
    }

    // Draw the text
    ctx.fillStyle = text
    ctx.fillText(label, x + 15, y + height / 2 + getMiddleCenterBias(ctx, theme))

    return true
  },
  isMatch: (c): c is PillCell => (c.data as PillCellProps).kind === 'auto-tags-cell',
  kind: GridCellKind.Custom,
})
